import { email, helpers, maxLength, minLength, required, sameAs } from '@vuelidate/validators';
import settings from '~/config/settings';

export function useValidation() {
  const password = (value: string) => {
    const { minLength, minUppercase, minSpecialCharacters, minNumbers } = settings.passwordCriteria;
    const hasMinLength = value.length >= Number(minLength);
    const uppercaseMatches = (value.match(/[A-Z]/g) || []).length >= Number(minUppercase);
    const specialCharMatches =
      (value.match(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/g) || []).length >= Number(minSpecialCharacters);
    const numberMatches = (value.match(/\d/g) || []).length >= Number(minNumbers);
    return hasMinLength && uppercaseMatches && specialCharMatches && numberMatches;
  };

  const VALIDATIONS = {
    required: (label: string) => helpers.withMessage(() => `${label} is required`, required),
    email: () => helpers.withMessage(() => `Must be a valid email address`, email),
    minLength: (label: string, min: number) =>
      helpers.withMessage(
        ({ $params }) => `${label} should be at least ${$params.min} characters long`,
        minLength(min)
      ),
    maxLength: (label: string, min: number) =>
      helpers.withMessage(
        ({ $params }) => `${label} must be no more than ${$params.max} characters long`,
        maxLength(min)
      ),
    fileSize: (label: string, max: number) =>
      helpers.withMessage(
        ({ $params }) => `The file is too large. Please upload a file smaller than ${max}MB.`,
        function (file: File) {
          return file.size <= max * 1024 * 1024;
        }
      ),
    isImage: (label = 'file') =>
      helpers.withMessage(
        () => `The ${label} must be an image`,
        (value: File) => {
          return $isImageValidType(value);
        }
      ),
    password: () =>
      helpers.withMessage(
        () => `The Password must contain at least one uppercase and lowercase letter, digit, and special character`,
        password
      ),
    sameAs: (label: string, sameAsLabel: string, value: string | number) => {
      const sameAsPassword = () => sameAs(value);
      return helpers.withMessage(`${sameAsLabel} and ${label} don't match`, sameAsPassword());
    },
    minItems: (label: string, min: number) =>
      helpers.withMessage(({ $params }) => `${label} must be ${$params.max} items`, minLength(min)),
    maxItems: (label: string, min: number) =>
      helpers.withMessage(({ $params }) => `${label} must be no more than ${$params.max} items`, maxLength(min)),
  } as const;

  const PASSWORD_VALIDATION_STRICT = {
    required: VALIDATIONS.required('Password'),
    minLength: VALIDATIONS.minLength('Password', 8),
    password: VALIDATIONS.password(),
  } as const;

  const PASSWORD_VALIDATION = {
    required: VALIDATIONS.required('Password'),
    minLength: VALIDATIONS.minLength('Password', 8),
  } as const;

  return {
    validations: VALIDATIONS,
    VALIDATIONS,
    PASSWORD_VALIDATION_STRICT,
    PASSWORD_VALIDATION,
  };
}
